/**
 * Query hooks for the community register.
 *
 * Components use these, never the client directly. Every key carries the
 * active mahallu so nothing bleeds across a tenant switch.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api, unwrap } from "@/lib/api/client";
import type { components } from "@/lib/api/generated/schema";
import { useSession } from "@/lib/auth/session";
import { queryKeys } from "@/lib/query";

export type Area = components["schemas"]["AreaOut"];
export type House = components["schemas"]["HouseOut"];
export type Family = components["schemas"]["FamilyOut"];
export type Person = components["schemas"]["PersonOut"];
export type HouseStatus = components["schemas"]["HouseStatus"];

/**
 * Drop undefined keys before they reach the query string.
 *
 * The generated param types allow `string | null` but not `undefined`, and
 * under exactOptionalPropertyTypes a spread of a partially-filled filter object
 * does not satisfy them. Stripping is also what we want on the wire: an absent
 * filter should not appear as `?q=undefined`.
 */
type Compacted<T> = { [K in keyof T]?: Exclude<T[K], undefined> };

function compact<T extends object>(input: T): Compacted<T> {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as Compacted<T>;
}

function useMahalluId(): string {
  const { session } = useSession();
  return session?.active_mahallu_id ?? "none";
}

export function useAreas() {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.areas(mahalluId),
    queryFn: async () => unwrap(await api.GET("/api/v1/areas", {})),
    // Reference data changes rarely.
    staleTime: 5 * 60_000,
  });
}

export interface HouseFilters {
  q?: string | undefined;
  area_id?: string | undefined;
  status?: HouseStatus | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
}

export function useHouses(filters: HouseFilters) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.houses.list(mahalluId, filters),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/houses", {
          params: {
            query: compact({ ...filters, limit: filters.limit ?? 50 }),
          },
        }),
      ),
    // Keep the previous page on screen while the next one loads, so the table
    // does not blank between pages.
    placeholderData: (previous) => previous,
  });
}

/**
 * Houses for a picker, a page at a time.
 *
 * A mahallu runs to several hundred houses and grows every year, so a picker
 * cannot ask for all of them and cannot stop at the first fifty either -- the
 * house someone is looking for is as likely to be number four hundred. The
 * search narrows it, and scrolling to the end of the list fetches the next
 * page rather than silently ending there.
 */
export function useHousesForPicker(q: string | undefined, pageSize = 25) {
  const mahalluId = useMahalluId();
  return useInfiniteQuery({
    queryKey: queryKeys.houses.picker(mahalluId, q ?? ""),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/houses", {
          params: {
            query: compact({
              q,
              limit: pageSize,
              cursor: pageParam,
              // `as const`, or it widens to `string` and stops matching
              // the generated `"ACTIVE" | "INACTIVE" | null`.
              status: "ACTIVE" as const,
            }),
          },
        }),
      ),
    getNextPageParam: (last) =>
      last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined,
    // Nothing typed, nothing fetched. The list shows matches for a search,
    // not the first page of the register, so there is nothing to load until
    // there is something to match.
    enabled: Boolean(q),
  });
}

/**
 * Families for a picker, a page at a time. See `useHousesForPicker`.
 */
export function useFamiliesForPicker(q: string | undefined, pageSize = 25) {
  const mahalluId = useMahalluId();
  return useInfiniteQuery({
    queryKey: queryKeys.families.picker(mahalluId, q ?? ""),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/families", {
          params: { query: compact({ q, limit: pageSize, cursor: pageParam }) },
        }),
      ),
    getNextPageParam: (last) =>
      last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined,
    // Nothing typed, nothing fetched. The list shows matches for a search,
    // not the first page of the register, so there is nothing to load until
    // there is something to match.
    enabled: Boolean(q),
  });
}

/**
 * People for a picker, a page at a time.
 *
 * A mahallu of two thousand people cannot put its roll in a dropdown, and
 * cutting it at the first fifty hides everybody after that.
 */
export function usePeopleForPicker(q: string | undefined, pageSize = 25) {
  const mahalluId = useMahalluId();
  return useInfiniteQuery({
    queryKey: queryKeys.people.picker(mahalluId, q ?? ""),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/people", {
          params: { query: compact({ q, limit: pageSize, cursor: pageParam }) },
        }),
      ),
    getNextPageParam: (last) =>
      last.page.has_more ? (last.page.next_cursor ?? undefined) : undefined,
    // Nothing typed, nothing fetched. The list shows matches for a search,
    // not the first page of the register, so there is nothing to load until
    // there is something to match.
    enabled: Boolean(q),
  });
}

export function useHouse(houseId: string | undefined) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.houses.detail(mahalluId, houseId ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/houses/{house_id}", {
          params: { path: { house_id: houseId! } },
        }),
      ),
    enabled: Boolean(houseId),
  });
}

export interface FamilyFilters {
  q?: string | undefined;
  house_id?: string | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
}

export function useFamilies(filters: FamilyFilters) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.families.list(mahalluId, filters),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/families", {
          params: {
            query: compact({ ...filters, limit: filters.limit ?? 50 }),
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

export function useFamily(familyId: string | undefined) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.families.detail(mahalluId, familyId ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/families/{family_id}", {
          params: { path: { family_id: familyId! } },
        }),
      ),
    enabled: Boolean(familyId),
  });
}

export interface PersonFilters {
  q?: string | undefined;
  family_id?: string | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
}

export function usePeople(filters: PersonFilters) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.people.list(mahalluId, filters),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/people", {
          params: {
            query: compact({ ...filters, limit: filters.limit ?? 50 }),
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

export function useSummary() {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.summary(mahalluId),
    queryFn: async () => unwrap(await api.GET("/api/v1/summary", {})),
  });
}

export function useCreateHouse() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return useMutation({
    mutationFn: async (body: components["schemas"]["HouseCreate"]) =>
      unwrap(await api.POST("/api/v1/houses", { body })),
    onSuccess: () => {
      // Narrowest key that covers the change; a blanket invalidate is a
      // refetch storm.
      void queryClient.invalidateQueries({ queryKey: ["houses", mahalluId] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.areas(mahalluId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.summary(mahalluId),
      });
    },
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return useMutation({
    mutationFn: async (body: components["schemas"]["FamilyCreate"]) =>
      unwrap(await api.POST("/api/v1/families", { body })),
    onSuccess: (family) => {
      void queryClient.invalidateQueries({ queryKey: ["families", mahalluId] });
      void queryClient.invalidateQueries({ queryKey: ["houses", mahalluId] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.summary(mahalluId),
      });
      return family;
    },
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return useMutation({
    mutationFn: async (body: components["schemas"]["PersonCreate"]) =>
      unwrap(await api.POST("/api/v1/people", { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["people", mahalluId] });
      void queryClient.invalidateQueries({ queryKey: ["families", mahalluId] });
      void queryClient.invalidateQueries({ queryKey: ["houses", mahalluId] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.summary(mahalluId),
      });
    },
  });
}

export function usePerson(personId: string | undefined) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: queryKeys.people.detail(mahalluId, personId ?? ""),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/people/{person_id}", {
          params: { path: { person_id: personId! } },
        }),
      ),
    enabled: Boolean(personId),
  });
}

export function useCreateArea() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return useMutation({
    mutationFn: async (body: components["schemas"]["AreaCreate"]) =>
      unwrap(await api.POST("/api/v1/areas", { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.areas(mahalluId),
      });
    },
  });
}

/* ------------------------------------------ edits and removals --------- */

/**
 * Nothing here is a plain delete.
 *
 * A house is retired rather than removed: certificates already in the
 * community quote its number, and that number is never reissued. A family or
 * a person is soft-deleted, and only when nothing depends on them -- a death
 * is recorded in the Death register, never by deleting the person. The API
 * enforces all of that; these hooks just carry the reason through.
 */

function useCommunityInvalidation() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["houses", mahalluId] });
    void queryClient.invalidateQueries({ queryKey: ["families", mahalluId] });
    void queryClient.invalidateQueries({ queryKey: ["people", mahalluId] });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.areas(mahalluId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.summary(mahalluId),
    });
  };
}

export function useUpdateArea() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["AreaUpdate"] & { id: string }) =>
      unwrap(
        await api.PATCH("/api/v1/areas/{area_id}", {
          params: { path: { area_id: id } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteArea() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE("/api/v1/areas/{area_id}", {
          params: { path: { area_id: id } },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateHouse() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["HouseUpdate"] & { id: string }) =>
      unwrap(
        await api.PATCH("/api/v1/houses/{house_id}", {
          params: { path: { house_id: id } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

/**
 * Deletes a house.
 *
 * The row goes. The server refuses while a family still lives there, and
 * refuses while a register entry or receipt still quotes the number -- saying
 * which in both cases. The number is not reissued afterwards: the next house
 * takes the highest used number plus one, so a deleted P31 leaves a gap.
 */
export function useDeleteHouse() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        await api.DELETE("/api/v1/houses/{house_id}", {
          params: { path: { house_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateFamily() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["FamilyUpdate"] & { id: string }) =>
      unwrap(
        await api.PATCH("/api/v1/families/{family_id}", {
          params: { path: { family_id: id } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

/**
 * Reassign the Grahanaadhan.
 *
 * Not a nicety. Finalizing a death or a viduthal for the head of a household
 * that still has members refuses with HEAD_REASSIGNMENT_REQUIRED -- the
 * software does not get to pick the next head, the mahallu does. Without this
 * the register entry could never be finalized from the app at all.
 */
export function useSetFamilyHead() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      familyId,
      personId,
    }: {
      familyId: string;
      personId: string;
    }) =>
      unwrap(
        await api.POST("/api/v1/families/{family_id}/head", {
          params: { path: { family_id: familyId } },
          body: { person_id: personId },
        }),
      ),
    onSuccess: invalidate,
  });
}

/** Move a family to another house, preserving the residence history. */
export function useMoveFamily() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      familyId,
      houseId,
      movedOn,
    }: {
      familyId: string;
      houseId: string;
      movedOn?: string | undefined;
    }) =>
      unwrap(
        await api.POST("/api/v1/families/{family_id}/move", {
          params: { path: { family_id: familyId } },
          body: {
            house_id: houseId,
            ...(movedOn ? { moved_on: movedOn } : {}),
          },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteFamily() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        await api.DELETE("/api/v1/families/{family_id}", {
          params: { path: { family_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdatePerson() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["PersonUpdate"] & { id: string }) =>
      unwrap(
        await api.PATCH("/api/v1/people/{person_id}", {
          params: { path: { person_id: id } },
          body,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeletePerson() {
  const invalidate = useCommunityInvalidation();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        await api.DELETE("/api/v1/people/{person_id}", {
          params: { path: { person_id: id } },
          body: { reason },
        }),
      ),
    onSuccess: invalidate,
  });
}

export type RegisterFamilyIn = components["schemas"]["RegisterFamilyIn"];
export type RegisterFamilyOut = components["schemas"]["RegisterFamilyOut"];
export type RegisterMemberIn = components["schemas"]["MemberIn"];

/**
 * The number the next new house in an area will get.
 *
 * The server assigns MAX(house_seq)+1 within the area -- never COUNT+1,
 * because numbers are not reissued -- so the highest house in the area, by
 * sequence, answers the question. Advisory: someone else saving first takes
 * it, and the success banner shows the number actually given.
 */
export function useNextHouseSeq(areaId: string | undefined) {
  const mahalluId = useMahalluId();
  return useQuery({
    queryKey: ["houses", mahalluId, "next-seq", areaId ?? ""] as const,
    queryFn: async () => {
      const page = unwrap(
        await api.GET("/api/v1/houses", {
          params: {
            // Filtered to one area, `-house_number` is descending house_seq.
            query: { area_id: areaId!, sort: "-house_number", limit: 1 },
          },
        }),
      );
      return (page.items[0]?.house_seq ?? 0) + 1;
    },
    enabled: Boolean(areaId),
  });
}

/** House, family and every member in one request, one transaction. */
export function useRegisterFamily() {
  const queryClient = useQueryClient();
  const mahalluId = useMahalluId();
  return useMutation({
    mutationFn: async (body: RegisterFamilyIn) =>
      unwrap(await api.POST("/api/v1/families/register", { body })),
    onSuccess: () => {
      for (const root of ["houses", "families", "people", "finance"]) {
        void queryClient.invalidateQueries({ queryKey: [root, mahalluId] });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.areas(mahalluId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.summary(mahalluId),
      });
    },
  });
}

/**
 * Hand a PDF to the browser's print dialog, from a hidden frame.
 *
 * Where the browser will not print a PDF in a frame, the file opens in a tab
 * instead. Mirrors the receipt printing in finance.
 */
function printPdf(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.tabIndex = -1;
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(url, "_blank", "noopener");
    }
    // Long enough for the print dialog to have read the file.
    window.setTimeout(() => {
      frame.remove();
      URL.revokeObjectURL(url);
    }, 60_000);
  };
  document.body.appendChild(frame);
}

export type FamilyPrint = "register" | "statement";

/**
 * Print a family's register sheet or its yearly maasavari statement.
 *
 * Fetched with the session's token -- a plain link cannot carry it.
 */
export function usePrintFamilyPdf() {
  return useMutation({
    mutationFn: async (args: { familyId: string; kind: FamilyPrint }) => {
      const { getAccessToken } = await import("@/lib/auth/token-store");
      const { API_BASE_URL, ApiError } = await import("@/lib/api/client");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/families/${args.familyId}/${args.kind}.pdf`,
        {
          credentials: "include",
          headers: { Authorization: `Bearer ${getAccessToken() ?? ""}` },
        },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => undefined)) as
          import("@/lib/api/client").ApiErrorBody | undefined;
        throw new ApiError(response.status, body);
      }
      printPdf(await response.blob());
    },
    meta: { silent: true },
  });
}
